import React, { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Button, Label, Segment } from 'semantic-ui-react';
import {
  AttachmentFormGroup,
  AttachmentWrapper,
  StyledDeleteButton
} from '~/pages/Dairy/Newsletter/CreateEditNewsletter/LangTab/Attachments/style';
import { FormFields } from '~/components/FormFields';
import FileUpload from '~/components/FileUpload';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import NewsletterService from '~/api/services/newsletter';
import { b64toBlob, handleErrors } from '~/utils/helpers';
import Dialog from '~/components/Dialog';

interface Props {
  languageIndex: number
}

const Attachments: React.FC<Props> = ({ languageIndex }): JSX.Element => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [attachmentIndex, setAttachmentIndex] = useState(null);
  const [fileSource, setFileSource] = useState(null);

  const {
    control,
    setValue,
    watch,
    getValues
  } = useFormContext()

  const attachments = useFieldArray({
    control,
    name: `languages[${languageIndex}].attachments`
  });

  const watchedAttachments = watch(`languages[${languageIndex}].attachments[${attachmentIndex}]`);

  useEffect(() => {
    getFileName(languageIndex, attachmentIndex);
  }, [watchedAttachments]);

  async function handleDownloadPdf(fileName, fileSrc) {
    try {
      if (!fileSrc?.toString().startsWith('data:')) {
        const response = await NewsletterService.getFileNewsletter(id, { fileName: fileName });
        const type = response.data.split(';')[0].split('data:')[1]
        const blob = b64toBlob(response.data, type);
        const blobUrl = URL.createObjectURL(blob);
        setFileSource(blobUrl);
      } else {
        setFileSource(fileSrc);
      }
    } catch (error) {
      handleErrors(error)
    }
  }

  function addAttachmentFile() {
    const attachmentFile = {
      attachment: '',
      originalFilename: ''
    };

    attachments.append(attachmentFile);
  }

  function removeAttachmentFile(attachmentIndex) {
    attachments.remove(attachmentIndex);
  }

  function getFileName(languageIndex, attachmentIndex) {
    return getValues(`languages[${languageIndex}].attachments[${attachmentIndex}].originalFilename`);
  }

  function renderModalPreviewFile() {
    return <Dialog
      open={Boolean(fileSource)}
      handleClose={() => setFileSource(null)}
      textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
    />
  }

  return (
    <>
      <Button
        onClick={addAttachmentFile}
        primary
        size='mini'
        type={'button'}
        labelPosition='right'
        content={t('Add new document')}
        icon='plus'
        disabled={attachments.fields.length >= 3}
      />

      {attachments?.fields?.map((attachment, attachmentIndex) => (
        <AttachmentWrapper key={attachment.id}>
          <Segment>
            {
              Boolean(getFileName(languageIndex, attachmentIndex)) &&
              <div style={{ margin: '1em', marginLeft: 0, display: 'flex', justifyContent: 'space-between' }}>
                <Label
                  basic
                  onClick={() => {
                    handleDownloadPdf(getValues(`languages[${languageIndex}].attachments[${attachmentIndex}].attachment`),
                      getValues(`languages[${languageIndex}].attachments[${attachmentIndex}].attachment`));
                  }}
                  color='blue'
                  as='a'
                  content={getFileName(languageIndex, attachmentIndex)}
                  icon='file'
                />
                <StyledDeleteButton
                  onClick={() => removeAttachmentFile(attachmentIndex)}
                  size={'small'}
                  basic
                  color='red'
                  type={'button'}
                  icon='trash' />
              </div>
            }
            <AttachmentFormGroup>
              <FormFields.External
                name={`languages[${languageIndex}].attachments[${attachmentIndex}].attachment`}
                control={control}
                keyName={'filename'}
                onChange={(value, name) => {
                  // setIsBusy(true);
                  setValue(`languages[${languageIndex}].attachments[${attachmentIndex}].attachment`, value);
                  setValue(`languages[${languageIndex}].attachments[${attachmentIndex}].originalFilename`, name);
                  setAttachmentIndex(attachmentIndex);
                  // setIsBusy(false);
                }}
              >
                <FileUpload
                  button
                  encodeFiles
                  accept={{
                    'application/pdf': ['.pdf']
                  }}
                  sizeLimitInMB={10}
                />
              </FormFields.External>
            </AttachmentFormGroup>
          </Segment>
        </AttachmentWrapper>
      ))}
      {fileSource && renderModalPreviewFile()}
    </>
  );
};

export default Attachments;
